/**
 * Shop Favorite Button Web Component
 * ------------------------------------
 * Toggles a user's favorite (save/unsave) via AJAX.
 * Renders a fragno-button with icon-only heart in Light DOM.
 *
 * Attributes:
 * - href: API path for POST/DELETE (omit for unauthenticated users)
 * - resource-name: Resource name for aria-label
 * - data-saved: Boolean attribute — present when already favorited
 * - sign-in-url: Redirect URL for unauthenticated users (omit for authenticated)
 *
 * Example:
 *   <shop-favorite-button
 *     href="/favorite/store/123"
 *     resource-name="Nike"
 *     data-saved>
 *   </shop-favorite-button>
 */

import { showToast } from "../../../modules/toast";
import { dispatchTrack } from "../../../analytics/shop-analytics-track.js";

const HEART_ROUNDED_PATH =
  "M16.1111 3C19.6333 3 22 6.3525 22 9.48C22 15.8138 12.1778 21 12 21C11.8222 21 2 15.8138 2 9.48C2 6.3525 4.36667 3 7.88889 3C9.91111 3 11.2333 4.02375 12 4.92375C12.7667 4.02375 14.0889 3 16.1111 3Z";

const HEART_ROUNDED_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${HEART_ROUNDED_PATH}"/></svg>`;

const HEART_ROUNDED_FILLED_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="${HEART_ROUNDED_PATH}"/></svg>`;

const ShopFavoriteButton = (function () {
  const TAG_NAME = "shop-favorite-button";

  class ShopFavoriteButton extends HTMLElement {
    static get observedAttributes() {
      return ["data-saved", "button-class"];
    }

    connectedCallback() {
      if (!this._initialized) {
        this._render();
        this._bindClick();
        this._initialized = true;
      }
    }

    disconnectedCallback() {
      this._removeClickListener();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this._initialized) return;

      // data-saved only swaps the icon + label, so mutate the existing
      // fragno-button in place. A full _render() would discard the focused
      // element on every save/unsave, dropping keyboard/AT focus.
      if (name === "data-saved") {
        this._updateButton();
      } else {
        this._render();
        this._bindClick();
      }
    }

    _heartSvg() {
      return this.hasAttribute("data-saved")
        ? HEART_ROUNDED_FILLED_SVG
        : HEART_ROUNDED_SVG;
    }

    _render() {
      const name = this.getAttribute("resource-name") || "";
      const isSaved = this.hasAttribute("data-saved");
      const label = isSaved ? `Remove ${name} from Saved` : `Save ${name}`;

      const btn = document.createElement("fragno-button");
      btn.setAttribute("icon-only", "");
      btn.setAttribute("size", "sm");
      btn.setAttribute("hierarchy", "secondary");
      btn.setAttribute("icon-leading", this._heartSvg());
      btn.setAttribute("label", label);
      btn.classList.add(
        this.getAttribute("button-class") || "store-page-header__favorite",
      );

      this.innerHTML = "";
      this.appendChild(btn);
    }

    _updateButton() {
      // _initialized === true normally implies a child fragno-button exists.
      // If that invariant is ever broken, fall back to a full (re-)render so
      // icon/label updates aren't silently dropped.
      const btn = this.querySelector("fragno-button");
      if (!btn) return this._render();

      const name = this.getAttribute("resource-name") || "";
      const isSaved = this.hasAttribute("data-saved");

      btn.setAttribute("icon-leading", this._heartSvg());
      btn.setAttribute(
        "label",
        isSaved ? `Remove ${name} from Saved` : `Save ${name}`,
      );
    }

    _bindClick() {
      this._removeClickListener();

      this._clickListener = (event) => {
        event.stopPropagation();
        this._handleClick();
      };

      this.addEventListener("fragno-button-click", this._clickListener);

      // The native click bubbles past this element too, and a card's
      // data-trigger="click" analytics listener up the tree would count a
      // save as a card click.
      this._nativeClickListener = (event) => event.stopPropagation();
      this.addEventListener("click", this._nativeClickListener);
    }

    _removeClickListener() {
      if (this._clickListener) {
        this.removeEventListener("fragno-button-click", this._clickListener);
        this._clickListener = null;
      }
      if (this._nativeClickListener) {
        this.removeEventListener("click", this._nativeClickListener);
        this._nativeClickListener = null;
      }
    }

    _handleClick() {
      if (this.hasAttribute("loading")) return;

      if (!this._isAuthenticated()) {
        this._trackEvent("save_attempt");
        const signInUrl = this.getAttribute("sign-in-url");
        if (signInUrl) {
          ShopFavoriteButton._redirect(signInUrl);
        }
        return;
      }

      const isSaved = this.hasAttribute("data-saved");
      this._trackEvent(isSaved ? "unsave" : "save");

      this.setAttribute("loading", "");
      this._toggle();
    }

    _trackEvent(eventName) {
      let params = {};
      try {
        params = JSON.parse(this.getAttribute("data-event-params") || "{}");
      } catch {
        // Malformed JSON — track with empty params rather than failing
      }

      dispatchTrack(this, eventName, params);
    }

    async _toggle() {
      const href = this.getAttribute("href");
      if (!href) return this.removeAttribute("loading");

      const wasSaved = this.hasAttribute("data-saved");
      const name = this.getAttribute("resource-name") || "";

      const ok = await ShopFavoriteButton._persistToggle({
        href,
        isSaved: !wasSaved,
        name,
        signInUrl: this.getAttribute("sign-in-url"),
      });

      if (ok) {
        if (wasSaved) this.removeAttribute("data-saved");
        else this.setAttribute("data-saved", "");
      }
      this.removeAttribute("loading");
    }

    /**
     * Shared API call for both <shop-favorite-button> and fragno card save buttons.
     * @param {Object} opts
     * @param {string} opts.href - API endpoint for POST/DELETE
     * @param {boolean} opts.isSaved - Desired new state (true = saving, false = removing)
     * @param {string} opts.name - Resource name for toast messages
     * @param {string} [opts.signInUrl] - Redirect URL on 401
     * @returns {Promise<boolean>} true on success, false on error/redirect
     */
    static async _persistToggle({ href, isSaved, name, signInUrl }) {
      const method = isSaved ? "POST" : "DELETE";
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      try {
        const response = await fetch(href, {
          method,
          headers: {
            "X-CSRF-Token": csrfToken || "",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (response.status === 401) {
          ShopFavoriteButton._redirect(signInUrl || "/session/new");
          return false;
        }

        if (response.ok) {
          showToast({
            message: isSaved
              ? `Saved "${name}"`
              : `Removed "${name}" from Saved`,
            tone: "success",
          });
          return true;
        }

        ShopFavoriteButton._showErrorToast(isSaved, name);
        return false;
      } catch (_error) {
        ShopFavoriteButton._showErrorToast(isSaved, name);
        return false;
      }
    }

    static _redirect(url) {
      window.location.href = url;
    }

    static _showErrorToast(isSaved, name) {
      const action = isSaved ? "saving" : "removing";
      const suffix = isSaved ? "" : " from Saved";
      showToast({
        message: `Error ${action} "${name}"${suffix}`,
        tone: "error",
      });
    }

    /** Handles fragno-save-button-toggle from hero/featured cards with built-in saveable. */
    static _handleCardSaveToggle(event) {
      const saveBtn = event.target;
      const card = saveBtn.closest("fragno-hero-card, fragno-featured-card");
      if (!card) return;

      const signInUrl = card.dataset.signInUrl;
      if (signInUrl) {
        saveBtn.toggleAttribute("saved");
        ShopFavoriteButton._redirect(signInUrl);
        return;
      }

      const href = card.dataset.favoriteHref;
      if (!href) return;

      const isSaved = event.detail.saved;
      const name =
        card.getAttribute("eyebrow") || card.getAttribute("store-text") || "";

      ShopFavoriteButton._persistToggle({ href, isSaved, name }).then((ok) => {
        if (!ok) saveBtn.toggleAttribute("saved");
      });
    }

    _isAuthenticated() {
      return document.body.classList.contains("logged-in");
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopFavoriteButton);
  }

  document.addEventListener(
    "fragno-save-button-toggle",
    ShopFavoriteButton._handleCardSaveToggle,
  );

  return ShopFavoriteButton;
})();

export { ShopFavoriteButton };
